import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, XCircle, Clock, Landmark, Send, AlertTriangle, ShieldCheck, Plus } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { AdviceButton, BeneficiaryGate, BulkPayoutsPanel, QueuedPhonePeActions, QueuedExportButton, RailPicker } from "./payouts/PayoutsExtras";

const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;
const MODES = ["IMPS", "NEFT", "RTGS"];
const PURPOSES = ["vendor bill", "payout", "refund", "salary"];
const LANES = ["pending", "queued_phonepe", "dispatched", "failed", "rejected", "all"];
const laneLabel = (s) => s === "queued_phonepe" ? "Queued · PhonePe" : s;

const laneMeta = (s) => ({
  dispatched: { label: "Dispatched", color: "#2FB999", Icon: CheckCircle2 },
  queued_phonepe: { label: "Queued · PhonePe Business", color: "#B794F4", Icon: Clock },
  rejected: { label: "Rejected", color: "#C0392B", Icon: XCircle },
  failed: { label: "Failed / Reversed", color: "#C0392B", Icon: AlertTriangle },
}[s] || { label: "Pending approval", color: "#C9A84C", Icon: Clock });

const providerTone = (p) => ["processed"].includes(p) ? "#2FB999" : ["failed", "reversed", "cancelled", "rejected"].includes(p) ? "#C0392B" : "#C9A84C";

export function PhonePeBadge({ className = "" }) {
  return (
    <span data-testid="powered-by-phonepe" className={`inline-flex items-center gap-2 rounded-sm border border-[#5F259F]/60 bg-[#5F259F]/15 px-2.5 py-1 ${className}`}>
      <span className="w-4 h-4 rounded-full bg-[#5F259F] grid place-items-center text-[9px] font-black text-white leading-none">पे</span>
      <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#F5F5F5]">Powered by <span className="text-[#C9A84C]">PhonePe</span></span>
    </span>
  );
}

const railLabel = (r) => (r.executed_rail || (r.phonepe_payout_id ? "phonepe" : r.razorpay_payout_id ? "razorpay" : "")) === "phonepe" ? "PhonePe Payouts" : (r.razorpay_payout_id ? "Razorpay (fallback)" : "—");

function ProviderBanner({ provider }) {
  if (!provider) return null;
  const pp = provider.phonepe || {};
  const ok = pp.payouts_enabled;
  const rzOk = provider.enabled;
  return (
    <div data-testid="payouts-provider-banner" className={`rounded-lg border px-5 py-4 flex items-start gap-3 ${ok ? "border-[#5F259F]/50 bg-[#5F259F]/5" : "border-[#C9A84C]/40 bg-[#C9A84C]/5"}`}>
      {ok ? <ShieldCheck size={18} className="text-[#2FB999] mt-0.5" /> : <AlertTriangle size={18} className="text-[#C9A84C] mt-0.5" />}
      <div className="text-sm flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-semibold text-[#F5F5F5]" data-testid="payouts-primary-rail">PhonePe Payouts · {ok ? `primary rail · merchant ${pp.merchant_id || "—"} · ${pp.env}` : "credentials missing"}</span>
          <PhonePeBadge />
        </div>
        <div className="text-[#888888] mt-1">
          {ok ? "Every vendor disbursement is dispatched through PhonePe Payouts first. " : "Set PHONEPE_CLIENT_ID / PHONEPE_CLIENT_SECRET to enable the primary rail. "}
          Razorpay{rzOk ? ` (${provider.mode} · account ${provider.account_masked})` : ""} stays as a silent fallback{rzOk ? "" : " — currently not configured"}. Statuses sync via webhook and the Sync buttons.
        </div>
      </div>
    </div>
  );
}

function InitiateDialog({ api, open, onClose, onDone, provider }) {
  const empty = { user_email: "", amount: "", purpose: "vendor bill", narration: "", mode: "IMPS", debit_wallet: false, admin_note: "", bank_holder_name: "", bank_account_number: "", bank_ifsc: "", bank_name: "" , rail: "phonepe" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [gate, setGate] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (override = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/initiate", { ...f, amount: Number(f.amount), override_verification: override });
      if (data.payout.status === "queued_phonepe") toast.success("Queued for PhonePe Business dispatch — pay from PhonePe Business, then confirm the UTR in the Queued · PhonePe lane.");
      else toast.success(`Payout dispatched via ${railLabel(data.payout)} · ${data.payout.phonepe_payout_id || data.payout.razorpay_payout_id || data.payout.provider_status}${data.payout.advice_no ? ` · Advice ${data.payout.advice_no}` : ""}`);
      setF(empty); setGate(null); onDone(); onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "beneficiary_unverified") { setGate(d); return; }
      toast.error(typeof d === "string" ? d : d?.message || "Could not initiate payout");
    }
    finally { setBusy(false); }
  };
  const field = "bg-black/40 border-[#333333] mt-1 text-sm";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent data-testid="payout-initiate-modal" className="bg-[#141414] border border-[#C9A84C]/40 text-[#F5F5F5] max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-3">Initiate vendor payout <PhonePeBadge /></DialogTitle>
          <DialogDescription className="text-sm text-[#888888]">Funds are dispatched through PhonePe Payouts immediately (Razorpay as silent fallback). Leave bank fields blank to use the vendor's linked bank on file.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label className="text-sm text-[#C9A84C]">Vendor email</Label><Input data-testid="payout-init-email" value={f.user_email} onChange={set("user_email")} placeholder="vendor@company.com" className={field} /></div>
          <div><Label className="text-sm text-[#C9A84C]">Amount (₹)</Label><Input data-testid="payout-init-amount" type="number" min="1" value={f.amount} onChange={set("amount")} className={field} /></div>
          <div><Label className="text-sm text-[#C9A84C]">Transfer mode</Label>
            <select data-testid="payout-init-mode" value={f.mode} onChange={set("mode")} className={`w-full rounded-md border px-3 py-2 ${field}`}>{MODES.map((m) => <option key={m}>{m}</option>)}</select></div>
          <div><Label className="text-sm text-[#C9A84C]">Payout purpose</Label>
            <select data-testid="payout-init-purpose" value={f.purpose} onChange={set("purpose")} className={`w-full rounded-md border px-3 py-2 ${field}`}>{PURPOSES.map((m) => <option key={m}>{m}</option>)}</select></div>
          <div><Label className="text-sm text-[#C9A84C]">Narration (bank statement)</Label><Input data-testid="payout-init-narration" value={f.narration} onChange={set("narration")} placeholder="Vinci vendor bill INV-104" className={field} /></div>
          <div><Label className="text-sm text-[#C9A84C]">Account holder</Label><Input data-testid="payout-init-holder" value={f.bank_holder_name} onChange={set("bank_holder_name")} className={field} /></div>
          <div><Label className="text-sm text-[#C9A84C]">Account number</Label><Input data-testid="payout-init-account" value={f.bank_account_number} onChange={set("bank_account_number")} className={`${field} font-mono`} /></div>
          <div><Label className="text-sm text-[#C9A84C]">IFSC</Label><Input data-testid="payout-init-ifsc" value={f.bank_ifsc} onChange={set("bank_ifsc")} className={`${field} font-mono uppercase`} /></div>
          <div><Label className="text-sm text-[#C9A84C]">Bank name</Label><Input data-testid="payout-init-bank" value={f.bank_name} onChange={set("bank_name")} className={field} /></div>
          <div className="sm:col-span-2"><Label className="text-sm text-[#C9A84C]">Admin note</Label><Textarea data-testid="payout-init-note" value={f.admin_note} onChange={set("admin_note")} className={field} /></div>
          <label className="sm:col-span-2 flex items-center gap-3 text-sm text-[#D4D4D4]">
            <input data-testid="payout-init-debit-wallet" type="checkbox" checked={f.debit_wallet} onChange={set("debit_wallet")} className="accent-[#C9A84C] w-4 h-4" />
            Also debit this amount from the vendor's Vinci wallet (closed-loop withdrawal)
          </label>
        </div>
        <div className="space-y-1.5"><Label className="text-sm text-[#C9A84C]">Payout rail</Label><RailPicker testid="payout-init-rail" value={f.rail} onChange={(v) => setF((x) => ({ ...x, rail: v }))} phonepeOk={!!provider?.phonepe?.payouts_enabled} razorpayOk={!!provider?.enabled} /></div>
        {gate && <BeneficiaryGate api={api} detail={gate} bank={{ holder_name: f.bank_holder_name, account_number: f.bank_account_number, ifsc: f.bank_ifsc }} userEmail={f.user_email} onCancel={() => setGate(null)} onResolved={({ override }) => { setGate(null); submit(override); }} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="border-[#333333] text-[#D4D4D4]">Cancel</Button>
          <Button data-testid="payout-init-submit" onClick={() => submit(false)} disabled={busy || !f.user_email || !f.amount || f.narration.length < 3} className="bg-[#C9A84C] hover:bg-[#D8B75B] text-black font-semibold text-sm">
            {busy ? "Dispatching…" : <><Send size={14} className="mr-2" /> Dispatch via {f.rail === "razorpay" ? "Razorpay" : "PhonePe"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayoutRow({ r, provider, api, onApprove, onReject, onSync, syncing, onDone }) {
  const bank = r.bank_snapshot || {};
  const m = laneMeta(r.status);
  return (
    <div data-testid={`awp-payout-${r.id}`} className="admin-card !p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="font-serif text-lg text-[#F5F5F5] flex items-center gap-2">{r.user_email}
            {r.origin === "admin_initiated" && <span className="text-sm text-[#C9A84C] border border-[#C9A84C]/40 rounded-full px-2 py-0.5">Admin initiated</span>}
          </div>
          <div className="text-sm text-[#888888] inline-flex items-center gap-2 mt-1">
            <Landmark size={14} className="text-[#C9A84C]" />
            {bank.holder_name} · {bank.bank_name || "Bank"} · IFSC {bank.ifsc} · AC ····{(bank.account_number || "").slice(-4)}
          </div>
          <div className="text-sm text-[#D4D4D4] mt-2 italic">"{r.purpose}"</div>
          {(r.razorpay_payout_id || r.phonepe_payout_id || r.executed_rail) && (
            <div className="text-sm font-mono mt-2 flex flex-wrap items-center gap-3" data-testid={`payout-provider-${r.id}`}>
              <span className={railLabel(r).startsWith("PhonePe") ? "text-[#B794F4]" : "text-[#888888]"} data-testid={`payout-rail-${r.id}`}>{railLabel(r)} {r.phonepe_payout_id || r.razorpay_payout_id || ""}</span>
              <span style={{ color: providerTone(r.provider_status) }} className="uppercase tracking-wider">{r.provider_status}</span>
              {r.utr_reference && <span className="text-[#2FB999]">UTR {r.utr_reference}</span>}
            </div>
          )}
          {r.failure_reason && <div className="text-sm text-[#C0392B] mt-1">{r.failure_reason}</div>}
          {r.last_provider_error && r.status === "pending" && <div className="text-sm text-[#C0392B] mt-1">Last attempt: {r.last_provider_error}</div>}
          {r.status === "queued_phonepe" && <div className="text-xs text-[#B794F4] mt-1" data-testid={`queued-reason-${r.id}`}>Awaiting dispatch from PhonePe Business{r.queued_reason ? ` · API: ${r.queued_reason}` : ""}</div>}
          {r.beneficiary_verification_label && <div className="text-xs mt-1 inline-flex items-center gap-1.5 text-[#2FB999]" data-testid={`payout-beneficiary-${r.id}`}><ShieldCheck size={12} /> {r.beneficiary_verification_label}</div>}
          {r.admin_note && <div className="mt-1 text-sm text-[#888888] italic">Admin note: {r.admin_note}</div>}
        </div>
        <div className="text-right">
          <div className="font-serif text-2xl text-[#C9A84C]">{inr(r.amount)}</div>
          <div className="text-sm text-[#888888]">{new Date(r.requested_at).toLocaleString()}</div>
          <div className="text-sm uppercase tracking-wider mt-1 inline-flex items-center gap-1.5" style={{ color: m.color }}><m.Icon size={14} /> {m.label}</div>
        </div>
      </div>
      <div className="mt-4 flex gap-2 justify-end flex-wrap">
        <AdviceButton api={api} row={r} />
        {r.status === "queued_phonepe" && <QueuedPhonePeActions api={api} row={r} onDone={onDone} />}
        {r.status !== "queued_phonepe" && (r.razorpay_payout_id || r.phonepe_payout_id) && (
          <Button data-testid={`awp-payout-sync-${r.id}`} variant="outline" onClick={() => onSync(r)} disabled={syncing === r.id} className="border-[#333333] text-[#D4D4D4] hover:border-[#C9A84C] text-sm">
            <RefreshCw size={14} className={`mr-1 ${syncing === r.id ? "animate-spin" : ""}`} /> Sync status
          </Button>
        )}
        {r.status === "pending" && (
          <>
            <Button data-testid={`awp-payout-approve-${r.id}`} onClick={() => onApprove(r)} disabled={!(provider?.enabled || provider?.phonepe?.payouts_enabled)} title={(provider?.enabled || provider?.phonepe?.payouts_enabled) ? "" : "No payout rail configured"} className="bg-[#2FB999] hover:bg-[#3FD9B0] text-black text-sm font-semibold">
              <CheckCircle2 size={14} className="mr-1" /> Approve &amp; dispatch
            </Button>
            <Button data-testid={`awp-payout-reject-${r.id}`} onClick={() => onReject(r)} variant="outline" className="border-[#C0392B]/50 text-[#C0392B] hover:bg-[#C0392B]/10 text-sm">
              <XCircle size={14} className="mr-1" /> Reject
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function AdminPayoutsDesk({ api, onCountChange, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ counts: {}, in_flight: 0, provider: null });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [target, setTarget] = useState(null);
  const [action, setAction] = useState(null);
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("IMPS");
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [initOpen, setInitOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/payouts?status=${status}`);
      setRows(data.rows || []);
      setMeta({ counts: data.counts || {}, in_flight: data.in_flight || 0, provider: data.provider });
      if (onCountChange) onCountChange((data.counts && data.counts.pending) || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load payouts"); }
    finally { setLoading(false); }
  }, [api, status, onCountChange]);
  useEffect(() => { load(); }, [load]);

  const [gate, setGate] = useState(null);
  const [rail, setRail] = useState("phonepe");
  const reset = () => { setTarget(null); setAction(null); setNote(""); setMode("IMPS"); setGate(null); setRail("phonepe"); };
  const run = async (override = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/payouts/${target.id}/${action}`, { admin_note: note, mode, override_verification: override, rail });
      toast.success(action !== "approve" ? `Payout rejected · ${target.user_email}` : data.status === "queued_phonepe" ? "Approved · queued for PhonePe Business dispatch (confirm UTR in the Queued lane)" : `Dispatched via ${railLabel(data)} · ${data.phonepe_payout_id || data.razorpay_payout_id || data.provider_status}`);
      reset(); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "beneficiary_unverified") { setGate(d); return; }
      toast.error(typeof d === "string" ? d : d?.message || `Could not ${action}`);
    }
    finally { setBusy(false); }
  };
  const sync = async (r) => {
    setSyncing(r.id);
    try { const { data } = await api.post(`/admin/payouts/${r.id}/sync`); toast.success(`${railLabel(data.payout)} says: ${data.payout.provider_status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(null); }
  };
  const syncAll = async () => {
    setSyncing("all");
    try { const { data } = await api.post("/admin/payouts/sync-all"); toast.success(`Synced ${data.synced} in-flight payouts · ${data.changed} changed`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-payouts-desk">
      {!embedded && (
        <div>
          <span className="text-sm uppercase tracking-wider text-[#C9A84C] inline-flex items-center gap-3">Finance desk · Vendor Payouts <PhonePeBadge /></span>
          <h2 className="font-serif text-3xl text-[#F5F5F5] mt-1">Vendor payouts</h2>
          <p className="text-sm text-[#888888] mt-2 max-w-2xl">Approve partner withdrawals or push vendor disbursements straight to their bank. Every rupee leaves through PhonePe Payouts (Razorpay standing by as a silent fallback) and is tracked here until the bank confirms the UTR.</p>
        </div>
      )}
      <ProviderBanner provider={meta.provider} />
      <BulkPayoutsPanel api={api} onDispatched={load} />
      <div className="flex items-center gap-2 flex-wrap">
        {LANES.map((s) => (
          <button key={s} data-testid={`awp-payouts-filter-${s}`} onClick={() => setStatus(s)}
            className={`px-4 py-2 text-sm uppercase tracking-wider rounded-lg border transition-colors ${status === s ? "border-[#C9A84C] text-[#C9A84C] bg-[#C9A84C]/10" : "border-[#333333] text-[#888888] hover:text-[#F5F5F5]"}`}>
            {laneLabel(s)}{meta.counts[s] !== undefined ? ` · ${meta.counts[s]}` : ""}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <QueuedExportButton api={api} count={meta.counts.queued_phonepe} />
          <Button data-testid="payouts-sync-all" variant="outline" onClick={syncAll} disabled={syncing === "all" || !meta.in_flight} className="border-[#333333] text-[#D4D4D4] hover:border-[#C9A84C] text-sm">
            <RefreshCw size={14} className={`mr-1 ${syncing === "all" ? "animate-spin" : ""}`} /> Sync in-flight ({meta.in_flight})
          </Button>
          <Button data-testid="payouts-initiate" onClick={() => setInitOpen(true)} className="bg-[#C9A84C] hover:bg-[#D8B75B] text-black text-sm font-semibold">
            <Plus size={14} className="mr-1" /> Initiate payout
          </Button>
        </div>
      </div>
      {loading ? <div className="p-10 text-center text-[#888888]"><Loader2 className="animate-spin inline" /></div>
        : rows.length === 0 ? <div className="admin-card text-center text-[#888888] italic">No payouts in this lane</div>
        : <div className="space-y-3">{rows.map((r) => <PayoutRow key={r.id} r={r} api={api} onDone={load} provider={meta.provider} syncing={syncing} onSync={sync} onApprove={(x) => { setTarget(x); setAction("approve"); }} onReject={(x) => { setTarget(x); setAction("reject"); }} />)}</div>}

      <InitiateDialog api={api} open={initOpen} onClose={() => setInitOpen(false)} onDone={load} provider={meta.provider} />

      <Dialog open={!!target} onOpenChange={(v) => { if (!v && !busy) reset(); }}>
        <DialogContent data-testid="awp-payout-action-modal" className="bg-[#141414] border border-[#C9A84C]/40 text-[#F5F5F5]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{action === "approve" ? `Approve & dispatch via ${rail === "razorpay" ? "Razorpay" : "PhonePe"}` : "Reject withdrawal"} · {target?.user_email}</DialogTitle>
            <DialogDescription className="text-sm text-[#D4D4D4] pt-2">
              {action === "approve"
                ? `Debit ${inr(target?.amount || 0)} from the partner wallet and dispatch a PhonePe payout to ${target?.bank_snapshot?.bank_name || "their bank"} (Razorpay fallback if PhonePe declines). If both rails decline, the wallet is credited back automatically.`
                : "Reject the request. The partner's wallet is untouched and they receive an explanation email."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {action === "approve" && (
              <div><Label className="text-sm text-[#C9A84C]">Transfer mode</Label>
                <select data-testid="awp-payout-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="w-full rounded-md border bg-black/40 border-[#333333] mt-1 px-3 py-2 text-sm">{MODES.map((m) => <option key={m}>{m}</option>)}</select></div>
            )}
            <div><Label className="text-sm text-[#C9A84C]">Admin note</Label>
              <Textarea data-testid="awp-payout-note" value={note} onChange={(e) => setNote(e.target.value)} className="bg-black/40 border-[#333333] mt-1 text-sm" placeholder={action === "approve" ? "e.g. Settlement for September" : "Reason for rejection"} /></div>
          </div>
          {action === "approve" && <div className="space-y-1.5"><Label className="text-sm text-[#C9A84C]">Payout rail</Label><RailPicker testid="payout-approve-rail" value={rail} onChange={setRail} phonepeOk={!!meta.provider?.phonepe?.payouts_enabled} razorpayOk={!!meta.provider?.enabled} /></div>}
          {gate && target && <BeneficiaryGate api={api} detail={gate} bank={target.bank_snapshot || {}} userEmail={target.user_email} onCancel={() => setGate(null)} onResolved={({ override }) => { setGate(null); run(override); }} />}
          <DialogFooter>
            <Button variant="outline" onClick={reset} disabled={busy} className="border-[#333333] text-[#D4D4D4]">Cancel</Button>
            <Button data-testid="awp-payout-confirm" onClick={() => run(false)} disabled={busy} className={action === "approve" ? "bg-[#2FB999] hover:bg-[#3FD9B0] text-black text-sm font-semibold" : "bg-[#C0392B] hover:bg-[#DE4B3C] text-white text-sm font-semibold"}>
              {busy ? "Working…" : action === "approve" ? "Dispatch funds" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
